// Éléments communs des écrans de supervision (lot L6-A) : navigation interne, état honnête des sources
// (date du dernier calcul réussi, erreur de rafraîchissement, source absente, couverture), filtres de
// territoire et de période. Composants et styles existants, aucun nouveau design.
import { LIBELLES_VUES_AGREGATS, type CouvertureSupervision } from '@cc/contrats';
import { Bandeau, Champ, Lien, Selection } from '@cc/ui';
import type { Filtres } from './api';
import { dateCalcul } from './logique';
import styles from '../achats/achats.module.css';
import propres from './supervision.module.css';

const ENTREES = [
  { id: 'repertoire', libelle: 'Répertoire et carte', href: '/supervision' },
  { id: 'deploiement', libelle: 'Déploiement', href: '/supervision/deploiement' },
  { id: 'indicateurs', libelle: 'Indicateurs et comparaisons', href: '/supervision/indicateurs' },
  { id: 'competences', libelle: 'Compétences transférées', href: '/supervision/competences' },
  { id: 'exports', libelle: 'Exports', href: '/supervision/exports' },
] as const;

export function NavigationSupervision({ actif }: { actif: (typeof ENTREES)[number]['id'] }) {
  return (
    <nav className={styles.navigation} aria-label="Supervision">
      {ENTREES.map((e) => (
        <Lien key={e.id} href={e.href} aria-current={e.id === actif ? 'page' : undefined} className={e.id === actif ? styles.lienActif : styles.lien}>
          {e.libelle}
        </Lien>
      ))}
    </nav>
  );
}

/** Provinces présentes dans le répertoire du périmètre ; la province est imposée hors périmètre national. */
export function provincesDu(perimetre: string | undefined, lignes: { province_code: string; province_nom?: string }[]): { provinces: { code: string; nom: string }[]; imposee: string | null } {
  const provinces = [...new Map(lignes.map((l) => [l.province_code, { code: l.province_code, nom: l.province_nom ?? l.province_code }])).values()].sort((a, b) => a.code.localeCompare(b.code));
  return { provinces, imposee: perimetre && perimetre !== 'national' ? (provinces[0]?.code ?? null) : null };
}

/** État des sources : jamais un zéro silencieux à la place d'une source absente ou en erreur. */
export function EtatSources({ couverture }: { couverture: CouvertureSupervision | undefined }) {
  if (!couverture) return null;
  const sources = couverture.sources ?? [];
  const enErreur = sources.filter((s) => s.etat === 'erreur');
  const absentes = sources.filter((s) => !s.calcule_le || s.etat === 'sources_absentes');
  const globale = sources.length === 0 && !['rafraichie', 'referentiel'].includes(couverture.etat);
  return (
    <>
      {globale ? <Bandeau type={couverture.etat === 'erreur' ? 'attention' : 'erreur'}>État des agrégats : {couverture.etat}{couverture.message ? ` (${couverture.message})` : ''}.</Bandeau> : null}
      {absentes.length > 0 ? (
        <Bandeau type="erreur">
          Source absente : {absentes.map((s) => LIBELLES_VUES_AGREGATS[s.vue]).join(', ')}. Les valeurs correspondantes sont indisponibles, jamais remplacées par zéro.
        </Bandeau>
      ) : null}
      {enErreur.length > 0 ? (
        <Bandeau type="attention">
          Dernier rafraîchissement en erreur : {enErreur.map((s) => `${LIBELLES_VUES_AGREGATS[s.vue]} (${s.message ?? 'sans message'})`).join(' ; ')}. Les valeurs affichées datent du dernier calcul réussi ({enErreur.map((s) => dateCalcul(s.calcule_le)).join(', ')}).
        </Bandeau>
      ) : null}
      <p className={propres.mention} data-testid="couverture">
        Données calculées le {dateCalcul(couverture.calcule_le)}
        {couverture.collectivites_perimetre !== undefined ? ` · couverture : ${couverture.collectivites_couvertes ?? 0} collectivité(s) disponibles sur ${couverture.collectivites_perimetre}` : ''}
      </p>
    </>
  );
}

export function FiltresTerritoire({ filtres, onChange, avecPeriode = false, provinces, imposee }: { filtres: Filtres; onChange: (f: Filtres) => void; avecPeriode?: boolean; provinces: { code: string; nom: string }[]; imposee: string | null }) {
  return (
    <div className={styles.grille} role="group" aria-label="Filtres">
      <Selection
        libelle="Province"
        optionVide={imposee ? undefined : 'Toutes les provinces du périmètre'}
        options={provinces.filter((p) => !imposee || p.code === imposee).map((p) => ({ valeur: p.code, libelle: p.nom }))}
        value={imposee ?? filtres.province ?? ''}
        disabled={Boolean(imposee)}
        onChange={(e) => onChange({ ...filtres, province: e.target.value || undefined })}
      />
      <Selection
        libelle="Type de collectivité"
        optionVide="Tous les types"
        options={[{ valeur: 'commune', libelle: 'Communes' }, { valeur: 'conseil_departemental', libelle: 'Conseils départementaux' }]}
        value={filtres.type ?? ''}
        onChange={(e) => onChange({ ...filtres, type: (e.target.value || undefined) as Filtres['type'] })}
      />
      {avecPeriode ? (
        <>
          <Champ libelle="Du mois" type="month" value={filtres.mois_debut ?? ''} onChange={(e) => onChange({ ...filtres, mois_debut: e.target.value || undefined })} />
          <Champ libelle="Au mois" type="month" value={filtres.mois_fin ?? ''} onChange={(e) => onChange({ ...filtres, mois_fin: e.target.value || undefined })} />
        </>
      ) : null}
    </div>
  );
}
